import { Inject, Injectable, PLATFORM_ID } from "@angular/core";
import { Observable, of } from "rxjs";
import {
  HttpClient,
  HttpEvent,
  HttpHeaders,
  HttpParams,
} from "@angular/common/http";
import { catchError, map, tap, timeout, finalize } from "rxjs/operators";
import { isPlatformBrowser } from "@angular/common";
import { NavigationEnd, Router } from "@angular/router";
import { FlowQueryModel } from "../models/flowquerymodel";
import { Endpoint } from "../shared/endpoints";
import { environment } from "src/environments/environment";
import { buildAccessContextHeaders } from "../shared/utils/device-access.util";
import { OrgContextService } from "./org-context.service";

@Injectable({
  providedIn: "root",
})
export class FlowServiceService {
  constructor(
    private http: HttpClient,
    @Inject(PLATFORM_ID) private platformId: Object,
    private router: Router,
    private orgContextService: OrgContextService
  ) {}

  searchInFlows(flowQueryModel: FlowQueryModel) {
    const header = new HttpHeaders();
    header.append("Accept", "application/json");
    header.append("Content-Type", "application/json");
    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.searchFlowsUrl,
      flowQueryModel,
      {
        headers: header,
      }
    );
  }
  searchInBlogs(flowQueryModel: FlowQueryModel) {
    const header = new HttpHeaders();
    header.append("Accept", "application/json");
    header.append("Content-Type", "application/json");
    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.searchBlogsUrl,
      flowQueryModel,
      {
        headers: header,
      }
    );
  }

  searchInBundles(flowQueryModel: FlowQueryModel) {
    const header = new HttpHeaders();
    header.append("Accept", "application/json");
    header.append("Content-Type", "application/json");
    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.searchBundlesUrl,
      flowQueryModel,
      {
        headers: header,
      }
    );
  }

  searchInItemsRequired(flowQueryModel: FlowQueryModel) {
    const header = new HttpHeaders();
    header.append("Accept", "application/json");
    header.append("Content-Type", "application/json");
    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.searchItemsRequiredUrl,
      flowQueryModel,
      {
        headers: header,
      }
    );
  }

  getSearchApprovedFlowsByFlowQueryModel(flowQueryModel: FlowQueryModel) {
    const header = new HttpHeaders();
    header.append("Accept", "application/json");
    header.append("Content-Type", "application/json");
    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.getSearchApprovedFlowsByFlowQueryModel,
      flowQueryModel,
      {
        headers: header,
      }
    );
  }

  getFlowsByItemsRequired(flowQueryModel: FlowQueryModel) {
    const header = new HttpHeaders();
    header.append("Accept", "application/json");
    header.append("Content-Type", "application/json");
    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.getFlowsByItemsRequired,
      flowQueryModel,
      {
        headers: header,
      }
    );
  }

  addSupportFaq(flowQueryModel: any, value) {
    const header = new HttpHeaders();
    header.append("Accept", "application/json");
    header.append("Content-Type", "application/json");
    header.append("emailAddress", value);
    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.SupportFaq,
      flowQueryModel,
      {
        headers: header,
      }
    );
  }

  getAboutUs(queryModel): Observable<any> {
    const header = new HttpHeaders();
    header.append("Accept", "application/json");
    header.append("Content-Type", "application/json");

    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.getAboutUs,
      queryModel,
      {
        headers: header,
      }
    );
  }
  deleteInapproperiateFlow(queryModel): Observable<any> {
    const header = new HttpHeaders();
    header.append("Accept", "application/json");
    header.append("Content-Type", "application/json");

    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.deleteInapproperiateFlow,
      queryModel,
      {
        headers: header,
      }
    );
  }
  getAboutUsDetails(queryModel): Observable<any> {
    const header = new HttpHeaders();
    header.append("Accept", "application/json");
    header.append("Content-Type", "application/json");

    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.getAboutUsDetails,
      queryModel,
      {
        headers: header,
      }
    );
  }
  createAboutUs(queryModel: any) {
    const header = new HttpHeaders();
    header.append("Accept", "application/json");
    header.append("Content-Type", "application/json");
    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.CreateAboutUs,
      queryModel,
      {
        headers: header,
      }
    );
  }

  deleteSupportFaq(queryModel: any) {
    const headers = new HttpHeaders({
      Accept: "application/json",
      "Content-Type": "application/json",
    });

    return this.http.delete<any>(
      environment.apiBaseUrl + Endpoint.deleteSupportFaq,
      {
        headers: headers,
        body: queryModel, // `body` is correctly used for DELETE with a request payload
      }
    );
  }

  deleteBlog(queryModel: any) {
    const headers = new HttpHeaders({
      Accept: "application/json",
      "Content-Type": "application/json",
    });

    return this.http.delete<any>(environment.apiBaseUrl + Endpoint.deleteBlog, {
      headers: headers,
      body: queryModel, // `body` is correctly used for DELETE with a request payload
    });
  }

  getSupportFaq(flowQueryModel): Observable<any> {
    const header = new HttpHeaders();
    header.append("Accept", "application/json");
    header.append("Content-Type", "application/json");

    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.getSupportFaq,
      flowQueryModel,
      {
        headers: header,
      }
    );
  }
  getFaqDetailsByQuestion(flowQueryModel): Observable<any> {
    const header = new HttpHeaders();
    header.append("Accept", "application/json");
    header.append("Content-Type", "application/json");

    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.getFaqDetailsByQuestion,
      flowQueryModel,
      {
        headers: header,
      }
    );
  }
  getSearchApprovedBundleByBundleQueryModel(flowQueryModel: FlowQueryModel) {
    const header = new HttpHeaders();
    header.append("Accept", "application/json");
    header.append("Content-Type", "application/json");
    return this.http.post<any>(
      environment.apiBaseUrl +
        Endpoint.getSearchApprovedBundleByBundleQueryModel,
      flowQueryModel,
      {
        headers: header,
      }
    );
  }

  postFollowUser(userEmail, customerEmail) {
    let header = new HttpHeaders();
    header = header.append("Accept", "application/json");
    header = header.append("Content-Type", "application/json");
    header = header.append("userEmail", userEmail);
    header = header.append("followerEmail", customerEmail);
    return this.http.get<any>(
      environment.apiBaseUrl + Endpoint.postFollowUserUrl,
      {
        headers: header,
      }
    );
  }
  deleteTransferOwnershipByKey(userEmail, contentId) {
    let header = new HttpHeaders();
    header = header.append("Accept", "application/json");
    header = header.append("Content-Type", "application/json");
    header = header.append("userEmail", userEmail);
    header = header.append("contentId", contentId);
    return this.http.get<any>(
      environment.apiBaseUrl + Endpoint.deleteTransferOwnershipByKey,
      {
        headers: header,
      }
    );
  }
  getTransferOwnershipByKey(userEmail, contentId) {
    let header = new HttpHeaders();
    header = header.append("Accept", "application/json");
    header = header.append("Content-Type", "application/json");
    header = header.append("userEmail", userEmail);
    header = header.append("contentId", contentId);
    return this.http.get<any>(
      environment.apiBaseUrl + Endpoint.getTransferOwnershipByKey,
      {
        headers: header,
      }
    );
  }

  getTransferOwnershipByContentId(contentId) {
    let header = new HttpHeaders();
    header = header.append("Accept", "application/json");
    header = header.append("Content-Type", "application/json");
    return this.http.get<any>(
      environment.apiBaseUrl +
        Endpoint.getTransferOwnershipByContentId +
        `/${contentId}`,
      {
        headers: header,
      }
    );
  }
  setScrollTop() {
    // Scroll to top only once when explicitly called by a component.
    // Do NOT subscribe to router events here, otherwise every navigation
    // (or some dynamic updates) can unexpectedly jump the page to top.
    if (isPlatformBrowser(this.platformId)) {
      window.scroll(0, 0);
    }
  }

  publishUserFlow(flowModel, value): Observable<HttpEvent<any>> {
    this.orgContextService.applyToPayload(flowModel);

    const header = new HttpHeaders()
      .set("Accept", "application/json")
      .set("Content-Type", "application/json")
      .set("emailAddress", value);

    // If this is an existing flow (has rowKey), use versioning endpoint so save/publish creates a version
    if (flowModel && (flowModel.rowKey || flowModel.RowKey)) {
      const logicalFlowId = (flowModel.rowKey || flowModel.RowKey).toString();
      const versionRequest = {
        logicalFlowId: logicalFlowId,
        updatedFlow: flowModel,
      };

      return this.http.post<any>(
        environment.apiBaseUrl + Endpoint.createFlowVersionUrl,
        versionRequest,
        {
          headers: header,
          reportProgress: true,
          observe: "events",
        }
      );
    }

    // New flow: use original create endpoint
    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.createFlowUrl,
      flowModel,
      {
        headers: header,
        reportProgress: true,
        observe: "events",
      }
    );
  }
  publishBlog(flowModel, value): Observable<HttpEvent<any>> {
    const header = new HttpHeaders();
    header.append("Accept", "application/json");
    header.append("Content-Type", "application/json");
    header.append("emailAddress", value);
    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.createBlog,
      flowModel,
      {
        headers: header,
        reportProgress: true, // ✅ Enables progress tracking
        observe: "events", // ✅ Allows listening to upload progress
      }
    );
  }
  postTransferOwnership(ownershipModel, value): Observable<HttpEvent<any>> {
    const header = new HttpHeaders();
    header.append("Accept", "application/json");
    header.append("Content-Type", "application/json");
    header.append("emailAddress", value);
    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.postTransferOwnership,
      ownershipModel,
      {
        headers: header,
        reportProgress: true, // ✅ Enables progress tracking
        observe: "events", // ✅ Allows listening to upload progress
      }
    );
  }
  getUserTransferOwnershipDetails(
    ownershipModel,
    value
  ): Observable<HttpEvent<any>> {
    const header = new HttpHeaders();
    header.append("Accept", "application/json");
    header.append("Content-Type", "application/json");
    header.append("emailAddress", value);
    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.getUserTransferOwnershipDetails,
      ownershipModel
    );
  }

  flowCheckout(checkoutModel, value) {
    const header = new HttpHeaders();
    header.append("Accept", "application/json");
    header.append("Content-Type", "application/json");
    header.append("emailAddress", value);
    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.flowCheckoutUrl,
      checkoutModel,
      {
        headers: header,
      }
    );
  }

  saveFlowToServerAccessLevel(flowModel, value) {
    const header = new HttpHeaders();
    header.append("Accept", "application/json");
    header.append("Content-Type", "application/json");
    header.append("emailAddress", value);
    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.postAccessType,
      flowModel,
      {
        headers: header,
      }
    );
  }

  getCouponCodeDiscountValue(flowQueryModel): Observable<any> {
    const header = new HttpHeaders();
    header.append("Accept", "application/json");
    header.append("Content-Type", "application/json");

    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.getCouponCodeDiscountValueUrl,
      flowQueryModel,
      {
        headers: header,
      }
    );
  }

  updateFlowTime(
    userEmail: string,
    timeSpentOnFlow: number,
    flowId: number
  ): Observable<any> {
    const model = {
      UserEmail: userEmail,
      TimeSpentOnFlow: timeSpentOnFlow,
      FlowId: flowId,
    };

    return this.http.put(
      environment.apiBaseUrl + Endpoint.updateTimeSpentOnFlowUrl,
      model
    );
  }

  getUserDashboard(flowQueryModel): Observable<any> {
    const header = new HttpHeaders();
    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.getUserDashboardUrl,
      flowQueryModel,
      {
        headers: header,
      }
    );
  }

  getAllUserData(flowQueryModel): Observable<any> {
    const header = new HttpHeaders();
    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.getAllUserData,
      flowQueryModel,
      {
        headers: header,
      }
    );
  }
  getUserPermissionsData(flowQueryModel): Observable<any> {
    const header = new HttpHeaders();
    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.getUserPermissionsDetails,
      flowQueryModel,
      {
        headers: header,
      }
    );
  }

  getAllPermissionsDetail(): Observable<any> {
    const header = new HttpHeaders();
    return this.http.get<any>(
      environment.apiBaseUrl + Endpoint.getAllPermissionsDetail,
      {
        headers: header,
      }
    );
  }

  getUserPermissionsDetailsByRole(flowQueryModel): Observable<any> {
    const header = new HttpHeaders();
    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.getUserPermissionsDetailsByRole,
      flowQueryModel,
      {
        headers: header,
      }
    );
  }

  updateUserRoleDetail(flowQueryModel): Observable<any> {
    const header = new HttpHeaders();
    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.updateUserRoleDetail,
      flowQueryModel,
      {
        headers: header,
      }
    );
  }
  createPermissionsDetail(flowQueryModel): Observable<any> {
    const header = new HttpHeaders();
    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.createPermissionsDetail,
      flowQueryModel,
      {
        headers: header,
      }
    );
  }

  getAllPurchaseDetails(flowQueryModel): Observable<any> {
    const header = new HttpHeaders();
    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.getAllPurchaseDetails,
      flowQueryModel,
      {
        headers: header,
      }
    );
  }
  getAllReportedContent(flowQueryModel): Observable<any> {
    const header = new HttpHeaders();
    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.getAllReportedContent,
      flowQueryModel,
      {
        headers: header,
      }
    );
  }

  getUserDashboardFlows(flowQueryModel): Observable<any> {
    const header = new HttpHeaders();
    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.getUserDashboardFlows,
      flowQueryModel,
      {
        headers: header,
      }
    );
  }

  getUserDashboardBundles(flowQueryModel): Observable<any> {
    const header = new HttpHeaders();
    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.getUserDashboardBundles,
      flowQueryModel,
      {
        headers: header,
      }
    );
  }

  getUserDashboardPaidFlows(flowQueryModel): Observable<any> {
    const header = new HttpHeaders();
    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.getUserDashboardPaidFlows,
      flowQueryModel,
      {
        headers: header,
      }
    );
  }

  getUserDashboardPaidBundles(flowQueryModel): Observable<any> {
    const header = new HttpHeaders();
    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.getUserDashboardPaidBundles,
      flowQueryModel,
      {
        headers: header,
      }
    );
  }

  getFollowerUsers(flowQueryModel): Observable<any> {
    const header = new HttpHeaders();
    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.getFollowersUrl,
      flowQueryModel,
      {
        headers: header,
      }
    );
  }

  getUsersVoucherCodes(loginModel): Observable<any> {
    const header = new HttpHeaders();

    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.getUsersVoucherCodesUrl,
      loginModel,
      {
        headers: header,
      }
    );
  }
  getVoucherCode(voucherCodeModel): Observable<any> {
    const header = new HttpHeaders();
    header.append("Accept", "application/json");
    header.append("Content-Type", "application/json");

    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.getVoucherCodeUrl,
      voucherCodeModel,
      {
        headers: header,
      }
    );
  }

  getFollowingUsers(flowQueryModel): Observable<any> {
    const header = new HttpHeaders();
    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.getFollowingUrl,
      flowQueryModel,
      {
        headers: header,
      }
    );
  }

  getNotifications(flowQueryModel): Observable<any> {
    let header = new HttpHeaders();
    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.getNotificationUrl,
      flowQueryModel,
      {
        headers: header,
      }
    );
  }
  MarkAsReadNotifications(flowQueryModel): Observable<any> {
    let header = new HttpHeaders();
    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.markAsReadNotificationsUrl,
      flowQueryModel,
      {
        headers: header,
      }
    );
  }

  getFlows(userEmail, pageIndex): Observable<any> {
    let header = new HttpHeaders();
    header = header.append("userName", userEmail);
    header = header.append("pageIndex", pageIndex);
    return this.http.get<any>(
      environment.apiBaseUrl + Endpoint.getFlowsByCategoriesUrl,
      {
        headers: header,
      }
    );
  }

  getFlowsByInputCategory(
    userEmail,
    inputCategory,
    pageIndex
  ): Observable<any> {
    let header = new HttpHeaders();
    header = header.append("userName", userEmail);
    header = header.append("inputCategory", inputCategory);
    header = header.append("pageIndex", pageIndex);

    return this.http.get<any>(
      environment.apiBaseUrl + Endpoint.getFlowsByInputCategoryUrl,
      {
        headers: header,
      }
    );
  }

  SearchFlows(
    userEmail,
    inputCategory,
    searchString,
    pageIndex
  ): Observable<any> {
    let header = new HttpHeaders();
    header = header.append("userName", userEmail);
    header = header.append("inputCategory", inputCategory);
    header = header.append("pageIndex", pageIndex);
    header = header.append("searchString", searchString);

    return this.http.get<any>(
      environment.apiBaseUrl + Endpoint.getSearchFlowsUrl,
      {
        headers: header,
      }
    );
  }

  // postLinkClick(linkClickDetails) {
  //   const header = new HttpHeaders();
  //   header.append("Accept", "application/json");
  //   header.append("Content-Type", "application/json");

  //   return this.http.post<any>(
  //     environment.apiBaseUrl + Endpoint.PostLinkClickUrl,
  //     linkClickDetails,
  //     {
  //       headers: header,
  //     }
  //   );
  // }

  // getLinkClickDetails(contentType, contentId): Observable<any> {
  //   let header = new HttpHeaders();
  //   header = header.append("contentType", contentType);
  //   header = header.append("contentId", contentId + '');
  //   return this.http.get<any>(environment.apiBaseUrl + Endpoint.GetLinkClickUrl, {
  //     headers: header,
  //   });
  // }

  getLinkClickDetails(flowModel): Observable<any> {
    const header = new HttpHeaders();
    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.GetLinkClickUrl,
      flowModel,
      {
        headers: header,
      }
    );
  }
  getAllLinkClickDetails(flowModel): Observable<any> {
    const header = new HttpHeaders();
    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.GetAllLinkClickUrl,
      flowModel,
      {
        headers: header,
      }
    );
  }
  GetAllPageViews(flowModel): Observable<any> {
    const header = new HttpHeaders();
    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.GetAllPageViews,
      flowModel,
      {
        headers: header,
      }
    );
  }
  postLinkClick(linkClickDetails): Observable<any> {
    const header = new HttpHeaders();
    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.PostLinkClickUrl,
      linkClickDetails,
      {
        headers: header,
      }
    );
  }
  postPageView(linkClickDetails) {
    const header = new HttpHeaders();
    header.append("Accept", "application/json");
    header.append("Content-Type", "application/json");

    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.PostPageViewUrl,
      linkClickDetails,
      {
        headers: header,
      }
    );
  }

  getPageViewDetails(getPageViewDetails): Observable<any> {
    const header = new HttpHeaders();
    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.GetPageViewDetailsUrl,
      getPageViewDetails,
      {
        headers: header,
      }
    );
  }
  getContentDetails(getPageViewDetails): Observable<any> {
    const header = new HttpHeaders();
    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.GetContentCount,
      getPageViewDetails,
      {
        headers: header,
      }
    );
  }
  getContent(getPageViewDetails): Observable<any> {
    const header = new HttpHeaders();
    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.GetContent,
      getPageViewDetails,
      {
        headers: header,
      }
    );
  }
  getBundleContentDetails(getPageViewDetails): Observable<any> {
    const header = new HttpHeaders();
    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.GetBundleContentCount,
      getPageViewDetails,
      {
        headers: header,
      }
    );
  }
  getPurchaseByConsult(getPageViewDetails): Observable<any> {
    const header = new HttpHeaders();
    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.GetPurchaseByConsult,
      getPageViewDetails,
      {
        headers: header,
      }
    );
  }

  reportFlow(flowModel): Observable<any> {
    const header = new HttpHeaders();
    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.inAppropriateUrl,
      flowModel,
      {
        headers: header,
      }
    );
  }

  checkIfUserReportedReview(reviewRowKey: string, flowRowKey: string): Observable<any> {
    const header = new HttpHeaders();
    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.checkIfUserReportedReviewUrl,
      {
        ReviewRowKey: reviewRowKey,
        FlowRowKey: flowRowKey
      },
      {
        headers: header,
      }
    );
  }

  deleteFlow(flowModel): Observable<any> {
    const header = new HttpHeaders();
    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.deleteFlowUrl,
      flowModel,
      {
        headers: header,
      }
    );
  }

  isFlowLiked(flowModel): Observable<any> {
    let header = new HttpHeaders();
    header = header.append("flowId", flowModel.FlowId.toString());
    header = header.append("userEmail", flowModel.LikedByUserEmail.toString());
    return this.http.get<any>(
      environment.apiBaseUrl + Endpoint.IsFlowLikedUrl,
      {
        headers: header,
      }
    );
  }

  getIsFlowViewed(flowModel): Observable<any> {
    let header = new HttpHeaders();
    header = header.append("flowId", flowModel.FlowId.toString());
    return this.http.get<any>(
      environment.apiBaseUrl + Endpoint.getIsFlowViewedUrl,
      {
        headers: header,
      }
    );
  }

  CheckIsUserFollowing(flowModel): Observable<any> {
    const header = new HttpHeaders();
    header.append("Accept", "application/json");
    header.append("Content-Type", "application/json");
    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.IsUserFollowingUrl,
      flowModel,
      {
        headers: header,
      }
    );
  }

  getUsersFlowComments(flowQueryModel: FlowQueryModel): Observable<any> {
    const header = new HttpHeaders();
    header.append("Accept", "application/json");
    header.append("Content-Type", "application/json");
    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.usersFlowCommentsUrl,
      flowQueryModel,
      {
        headers: header,
      }
    );
  }

  getFlowBookMarkedStatus(flowModel): Observable<any> {
    let header = new HttpHeaders();
    header = header.append("flowId", flowModel.FlowId.toString());
    header = header.append("userName", flowModel.CustomerEmail.toString());
    return this.http.get<any>(
      environment.apiBaseUrl + Endpoint.IsFlowBookMarkedStatusUrl,
      {
        headers: header,
      }
    );
  }

  postBookMarkFlow(flowModel) {
    const header = new HttpHeaders();
    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.PostBookMarkFlowUrl,
      flowModel,
      {
        headers: header,
      }
    );
  }

  LikeFlow(flowModel): Observable<any> {
    const header = new HttpHeaders();
    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.LikeFlowUrl,
      flowModel,
      {
        headers: header,
      }
    );
  }

  LikeComment(flowModel): Observable<any> {
    const header = new HttpHeaders();
    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.postLikeFlowCommentUrl,
      flowModel,
      {
        headers: header,
      }
    );
  }

  FollowUser(flowModel): Observable<any> {
    // let header = new HttpHeaders();
    // header = header.append("userEmail", flowModel.UserEmailAddress.toString());
    // header = header.append("followerEmail", flowModel.FollowerEmail.toString());
    // return this.http.get<any>(environment.apiBaseUrl + Endpoint.FollowUserUrl, {
    //   headers: header,
    // });
    const header = new HttpHeaders();
    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.FollowUserUrl,
      flowModel,
      {
        headers: header,
      }
    );
  }
  editComment(commentModel): Observable<any> {
    const header = new HttpHeaders();
    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.editCommentUrl,
      commentModel,
      {
        headers: header,
      }
    );
  }

  addComment(commentModel): Observable<any> {
    const header = new HttpHeaders();
    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.addFlowCommentUrl,
      commentModel,
      {
        headers: header,
      }
    );
  }

  deleteComment(flowQueryModel: FlowQueryModel): Observable<any> {
    // const header = new HttpHeaders();
    // header.append("Accept", "application/json");
    // header.append("Content-Type", "application/json");
    // header.append("userName", emailAddress);

    // return this.http.post<any>(
    //   environment.apiBaseUrl + Endpoint.deleteFlowCommentUrl,
    //   commentModel,
    //   {
    //     headers: header,
    //   }
    // );
    const header = new HttpHeaders();
    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.deleteFlowCommentUrl,
      flowQueryModel,
      {
        headers: header,
      }
    );
  }

  getApprovedFlows(flowQueryModel: FlowQueryModel): Observable<any> {
    const header = new HttpHeaders();
    header.append("Accept", "application/json");
    header.append("Content-Type", "application/json");
    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.approvedUrl,
      flowQueryModel,
      {
        headers: header,
      }
    );
  }

  getAllFlows(emailAddress, pageIndex): Observable<any> {
    let header = new HttpHeaders();
    header = header.set("userName", emailAddress);
    header = header.append("pageIndex", pageIndex);
    return this.http.get<any>(
      environment.apiBaseUrl + Endpoint.getAllFlowsUrl,
      {
        headers: header,
      }
    );
  }

  getAllUserFlows(flowQueryModel: FlowQueryModel): Observable<any> {
    const header = new HttpHeaders();
    header.append("Accept", "application/json");
    header.append("Content-Type", "application/json");
    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.getAllUserFlowsUrl,
      flowQueryModel,
      {
        headers: header,
      }
    );
  }

  /** Published guides by other organization members visible to the caller (guide pack picker). */
  getOrgTeamGuides(scope: {
    organizationId: string;
    applicationId?: string | null;
    teamId?: string | null;
  }): Observable<any> {
    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.getOrgTeamGuidesUrl,
      {
        OrganizationId: scope.organizationId,
        ApplicationId: scope.applicationId || null,
        TeamId: scope.teamId || null,
      }
    );
  }
  getPurchasedHistoryFunds(flowQueryModel: FlowQueryModel): Observable<any> {
    const header = new HttpHeaders();
    header.append("Accept", "application/json");
    header.append("Content-Type", "application/json");
    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.getPurchasedHistoryFunds,
      flowQueryModel,
      {
        headers: header,
      }
    );
  }
  getGiftedFundsByCustomerRowKey(flowQueryModel): Observable<any> {
    const header = new HttpHeaders();
    header.append("Accept", "application/json");
    header.append("Content-Type", "application/json");
    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.getFundByCustomerRowKey,
      flowQueryModel,
      {
        headers: header,
      }
    );
  }
  getConsultationByDate(flowQueryModel): Observable<any> {
    const header = new HttpHeaders();
    header.append("Accept", "application/json");
    header.append("Content-Type", "application/json");
    console.log(flowQueryModel);
    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.getConsultationsByDate,
      flowQueryModel,
      {
        headers: header,
      }
    );
  }

  getUserConsultation(flowQueryModel: FlowQueryModel): Observable<any> {
    const header = new HttpHeaders();
    header.append("Accept", "application/json");
    header.append("Content-Type", "application/json");
    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.getUserConsultations,
      flowQueryModel,
      {
        headers: header,
      }
    );
  }
  getConsultation(flowQueryModel: FlowQueryModel): Observable<any> {
    const header = new HttpHeaders();
    header.append("Accept", "application/json");
    header.append("Content-Type", "application/json");
    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.getConsultations,
      flowQueryModel,
      {
        headers: header,
      }
    );
  }
  userFeedback(flowQueryModel): Observable<any> {
    const header = new HttpHeaders();
    header.append("Accept", "application/json");
    header.append("Content-Type", "application/json");
    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.saveUserConsultationsFeedback,
      flowQueryModel,
      {
        headers: header,
      }
    );
  }
  contentCreatorFeedback(flowQueryModel): Observable<any> {
    const header = new HttpHeaders();
    header.append("Accept", "application/json");
    header.append("Content-Type", "application/json");
    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.saveContentCreatorConsultationsFeedback,
      flowQueryModel,
      {
        headers: header,
      }
    );
  }

  getConsultationByRowKey(flowQueryModel: FlowQueryModel): Observable<any> {
    const header = new HttpHeaders();
    header.append("Accept", "application/json");
    header.append("Content-Type", "application/json");
    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.getConsultationsByRowKey,
      flowQueryModel,
      {
        headers: header,
      }
    );
  }

  bookConsultation(flowQueryModel): Observable<any> {
    const header = new HttpHeaders();
    header.append("Accept", "application/json");
    header.append("Content-Type", "application/json");
    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.bookConsultation,
      flowQueryModel,
      {
        headers: header,
      }
    );
  }

  updateConsultationOccurrence(payload: {
    consultationRowKey: string;
    occurrence: string;
  }): Observable<any> {
    const headers = new HttpHeaders({
      Accept: "application/json",
      "Content-Type": "application/json",
    });
    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.updateConsultationOccurrence,
      {
        ConsultationRowKey: payload.consultationRowKey,
        Occurrence: payload.occurrence,
      },
      { headers }
    );
  }
  getSavedFlows(flowQueryModel: FlowQueryModel): Observable<any> {
    const header = new HttpHeaders();
    header.append("Accept", "application/json");
    header.append("Content-Type", "application/json");
    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.bookMarkUrl,
      flowQueryModel,
      {
        headers: header,
      }
    );
  }

  getPurchasedFlows(flowQueryModel: FlowQueryModel): Observable<any> {
    const header = new HttpHeaders();
    header.append("Accept", "application/json");
    header.append("Content-Type", "application/json");
    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.purchasedUrl,
      flowQueryModel,
      {
        headers: header,
      }
    );
  }

  getAllBlogs(): Observable<any> {
    const header = new HttpHeaders();
    header.append("Accept", "application/json");
    header.append("Content-Type", "application/json");
    return this.http.get<any>(environment.apiBaseUrl + Endpoint.getAllBlogs, {
      headers: header,
    });
  }

  getBlogsByEmbeddedContent(
    contentType: "flow" | "bundle",
    rowKey: string
  ): Observable<any> {
    const header = new HttpHeaders();
    header.append("Accept", "application/json");
    header.append("Content-Type", "application/json");
    const params = new HttpParams()
      .set("contentType", contentType)
      .set("rowKey", rowKey || "");
    return this.http.get<any>(
      environment.apiBaseUrl + Endpoint.getBlogsByEmbeddedContent,
      {
        headers: header,
        params,
      }
    );
  }

  getAllUserBlogs(flowQueryModel: FlowQueryModel): Observable<any> {
    const header = new HttpHeaders();
    header.append("Accept", "application/json");
    header.append("Content-Type", "application/json");
    let params = new HttpParams();
    Object.keys(flowQueryModel).forEach((key) => {
      if (flowQueryModel[key] !== null && flowQueryModel[key] !== undefined) {
        params = params.append(key, flowQueryModel[key]);
      }
    });

    return this.http.get<any>(
      environment.apiBaseUrl + Endpoint.getUserBlogsDetails,
      {
        headers: header,
        params: params,
      }
    );
  }

  getDraftFlows(flowQueryModel: FlowQueryModel): Observable<any> {
    const header = new HttpHeaders();
    header.append("Accept", "application/json");
    header.append("Content-Type", "application/json");
    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.draftUrl,
      flowQueryModel,
      {
        headers: header,
      }
    );
  }

  getFlowById(
    flowIdOrModel: string | FlowQueryModel,
    userEmail?: string,
    options?: {
      deviceId?: string;
      latitude?: number;
      longitude?: number;
      accuracy?: number;
      /** Return unpublished edits instead of the published version (editors and reviewers only). */
      draft?: boolean;
    }
  ): Observable<any> {
    const header = new HttpHeaders({
      Accept: "application/json",
      "Content-Type": "application/json",
      ...buildAccessContextHeaders(options),
    });

    const body =
      typeof flowIdOrModel === "string"
        ? { FlowId: flowIdOrModel, userEmail: userEmail ?? "" }
        : flowIdOrModel;

    let params = new HttpParams();
    if (options?.draft) {
      params = params.set("draft", "true");
    }
    if (options?.deviceId) {
      params = params.set("deviceId", options.deviceId);
    }
    if (options?.latitude != null && !Number.isNaN(options.latitude)) {
      params = params.set("latitude", String(options.latitude));
    }
    if (options?.longitude != null && !Number.isNaN(options.longitude)) {
      params = params.set("longitude", String(options.longitude));
    }
    if (options?.accuracy != null && !Number.isNaN(options.accuracy)) {
      params = params.set("accuracy", String(options.accuracy));
    }

    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.getFlowsByIdUrl,
      body,
      {
        headers: header,
        params,
      }
    );
  }

  saveStepProgress(progressModel: any): Observable<any> {
    const header = new HttpHeaders();
    header.append("Accept", "application/json");
    header.append("Content-Type", "application/json");

    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.saveStepProgressUrl,
      progressModel,
      {
        headers: header,
      }
    );
  }

  updateTaggedItem(model: {
    flowId: string;
    stepRowKey: string;
    itemIndex: number;
    updatedItem: any;
  }): Observable<any> {
    const headers = new HttpHeaders()
      .set('Accept', 'application/json')
      .set('Content-Type', 'application/json');

    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.updateTaggedItemUrl,
      model,
      { headers }
    );
  }

  getFlowStepProgress(flowId: string): Observable<any> {
    const header = new HttpHeaders();
    header.append("Accept", "application/json");
    header.append("Content-Type", "application/json");

    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.getFlowStepProgressUrl,
      { FlowId: flowId },
      {
        headers: header,
      }
    );
  }

  toggleStepBookmark(bookmarkModel: any): Observable<any> {
    const header = new HttpHeaders();
    header.append("Accept", "application/json");
    header.append("Content-Type", "application/json");

    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.toggleStepBookmarkUrl,
      bookmarkModel,
      {
        headers: header,
      }
    );
  }

  getFlowStepBookmarks(flowId: string): Observable<any> {
    const header = new HttpHeaders();
    header.append("Accept", "application/json");
    header.append("Content-Type", "application/json");

    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.getFlowStepBookmarksUrl,
      { FlowId: flowId },
      {
        headers: header,
      }
    );
  }

  getBundles(emailAddress, pageIndex): Observable<any> {
    let header = new HttpHeaders();
    header = header.set("userName", emailAddress);
    header = header.set("pageIndex", pageIndex);
    return this.http.get<any>(environment.apiBaseUrl + Endpoint.getBundleUrl, {
      headers: header,
    });
  }

  getUserPublicBundles(flowQueryModel: FlowQueryModel): Observable<any> {
    const header = new HttpHeaders();
    header.append("Accept", "application/json");
    header.append("Content-Type", "application/json");
    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.getUserPublicBundles,
      flowQueryModel,
      {
        headers: header,
      }
    );
  }

  getUserBundles(flowQueryModel: FlowQueryModel): Observable<any> {
    const header = new HttpHeaders();
    header.append("Accept", "application/json");
    header.append("Content-Type", "application/json");
    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.getUserBundleUrl,
      flowQueryModel,
      {
        headers: header,
      }
    );
  }
  getContentLikeCount(getPageViewDetails): Observable<any> {
    const header = new HttpHeaders();
    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.GetContentLikeCount,
      getPageViewDetails,
      {
        headers: header,
      }
    );
  }
  getContentSaveCount(getPageViewDetails): Observable<any> {
    const header = new HttpHeaders();
    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.GetContentSaveCount,
      getPageViewDetails,
      {
        headers: header,
      }
    );
  }
  getContentCommentCount(getPageViewDetails): Observable<any> {
    const header = new HttpHeaders();
    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.GetContentCommentCount,
      getPageViewDetails,
      {
        headers: header,
      }
    );
  }

  // New methods for enhanced access control
  getAccessControl(flowId: string) {
    const header = new HttpHeaders({
      Accept: "application/json",
      "Content-Type": "application/json",
    });
    return this.http.get<any>(
      `${environment.apiBaseUrl}${Endpoint.getAccessControlUrl}/${encodeURIComponent(
        flowId
      )}`,
      { headers: header }
    );
  }

  setRestrictedAccessOptions(flowModel: any, userEmail: string) {
    const header = new HttpHeaders();
    header.append("Accept", "application/json");
    header.append("Content-Type", "application/json");
    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.setRestrictedAccessOptionsUrl,
      flowModel,
      {
        headers: header,
      }
    );
  }

  checkFlowAccess(
    flowId: string,
    userEmail: string = null,
    options?: {
      deviceId?: string;
      latitude?: number;
      longitude?: number;
      accuracy?: number;
    }
  ) {
    const header = new HttpHeaders({
      Accept: "application/json",
      "Content-Type": "application/json",
      ...buildAccessContextHeaders(options),
    });

    let params = new HttpParams()
      .set("flowId", flowId)
      .set("userEmail", userEmail ?? "");

    if (options?.deviceId) {
      params = params.set("deviceId", options.deviceId);
    }
    if (options?.latitude != null && !Number.isNaN(options.latitude)) {
      params = params.set("latitude", String(options.latitude));
    }
    if (options?.longitude != null && !Number.isNaN(options.longitude)) {
      params = params.set("longitude", String(options.longitude));
    }
    if (options?.accuracy != null && !Number.isNaN(options.accuracy)) {
      params = params.set("accuracy", String(options.accuracy));
    }

    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.checkFlowAccessUrl,
      null,
      {
        headers: header,
        params: params,
      }
    );
  }

  requestFlowAccess(requestData: any) {
    const header = new HttpHeaders();
    header.append("Accept", "application/json");
    header.append("Content-Type", "application/json");
    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.requestFlowAccessUrl,
      requestData,
      {
        headers: header,
      }
    );
  }

  getAccessRequestsByOwner(emailAddress: string) {
    const header = new HttpHeaders();
    header.append("Accept", "application/json");
    header.append("Content-Type", "application/json");
    header.append("userName", emailAddress);
    return this.http.get<any>(
      environment.apiBaseUrl + Endpoint.getAccessRequestsByOwnerUrl,
      {
        headers: header,
      }
    );
  }

  getAccessRequestsByRequester() {
    const header = new HttpHeaders({
      Accept: "application/json",
      "Content-Type": "application/json",
    });
    return this.http.get<any>(
      environment.apiBaseUrl + Endpoint.getAccessRequestsByRequesterUrl,
      {
        headers: header,
      }
    );
  }

  approveAccessRequest(
    flowId: string,
    requesterEmail: string,
    responseMessage?: string
  ) {
    const header = new HttpHeaders({
      Accept: "application/json",
      "Content-Type": "application/json",
    });

    const params = new HttpParams()
      .set("flowId", flowId)
      .set("requesterEmail", requesterEmail);

    if (responseMessage) {
      params.set("responseMessage", responseMessage);
    }

    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.approveAccessRequestUrl,
      null,
      {
        headers: header,
        params: params,
      }
    );
  }

  rejectAccessRequest(
    flowId: string,
    requesterEmail: string,
    responseMessage?: string
  ) {
    const header = new HttpHeaders({
      Accept: "application/json",
      "Content-Type": "application/json",
    });

    const params = new HttpParams()
      .set("flowId", flowId)
      .set("requesterEmail", requesterEmail);

    if (responseMessage) {
      params.set("responseMessage", responseMessage);
    }

    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.rejectAccessRequestUrl,
      null,
      {
        headers: header,
        params: params,
      }
    );
  }

  // ─── Collaboration Requests ───────────────────────────────────────────────

  sendCollaborationRequest(requestData: any) {
    const header = new HttpHeaders({
      Accept: "application/json",
      "Content-Type": "application/json",
    });
    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.sendCollaborationRequestUrl,
      requestData,
      { headers: header }
    );
  }

  getCollaborationRequestsByOwner(emailAddress: string) {
    const header = new HttpHeaders({
      Accept: "application/json",
      "Content-Type": "application/json",
      userName: emailAddress,
    });
    return this.http.get<any>(
      environment.apiBaseUrl + Endpoint.getCollaborationRequestsByOwnerUrl,
      { headers: header }
    );
  }

  getCollaborationRequestsByRequester() {
    const header = new HttpHeaders({
      Accept: "application/json",
      "Content-Type": "application/json",
    });
    return this.http.get<any>(
      environment.apiBaseUrl + Endpoint.getCollaborationRequestsByRequesterUrl,
      { headers: header }
    );
  }

  approveCollaborationRequest(
    contentId: string,
    requesterEmail: string,
    responseMessage?: string
  ) {
    const header = new HttpHeaders({
      Accept: "application/json",
      "Content-Type": "application/json",
    });
    let params = new HttpParams()
      .set("contentId", contentId)
      .set("requesterEmail", requesterEmail);
    if (responseMessage) {
      params = params.set("responseMessage", responseMessage);
    }
    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.approveCollaborationRequestUrl,
      null,
      { headers: header, params }
    );
  }

  rejectCollaborationRequest(
    contentId: string,
    requesterEmail: string,
    responseMessage?: string
  ) {
    const header = new HttpHeaders({
      Accept: "application/json",
      "Content-Type": "application/json",
    });
    let params = new HttpParams()
      .set("contentId", contentId)
      .set("requesterEmail", requesterEmail);
    if (responseMessage) {
      params = params.set("responseMessage", responseMessage);
    }
    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.rejectCollaborationRequestUrl,
      null,
      { headers: header, params }
    );
  }

  withdrawCollaborationRequest(contentId: string, ownerEmail: string) {
    const header = new HttpHeaders({
      Accept: "application/json",
      "Content-Type": "application/json",
    });
    const params = new HttpParams()
      .set("contentId", contentId)
      .set("ownerEmail", ownerEmail);
    return this.http.delete<any>(
      environment.apiBaseUrl + Endpoint.withdrawCollaborationRequestUrl,
      { headers: header, params }
    );
  }

  // ─────────────────────────────────────────────────────────────────────────

  deleteAccessRequest(flowId: string, requesterEmail: string) {
    const header = new HttpHeaders({
      Accept: "application/json",
      "Content-Type": "application/json",
    });

    const params = new HttpParams()
      .set("flowId", flowId)
      .set("requesterEmail", requesterEmail);

    return this.http.delete<any>(
      environment.apiBaseUrl + Endpoint.deleteAccessRequestUrl,
      {
        headers: header,
        params: params,
      }
    );
  }

  createFlowShort(flowShortModel: any): Observable<any> {
    const header = new HttpHeaders();
    header.append("Accept", "application/json");
    header.append("Content-Type", "application/json");
    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.createFlowShortUrl,
      flowShortModel,
      {
        headers: header,
      }
    );
  }

  getShortsByUserEmail(userEmail: string): Observable<any> {
    const header = new HttpHeaders();
    header.append("Accept", "application/json");
    header.append("Content-Type", "application/json");
    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.getShortsByUserEmailUrl,
      userEmail,
      {
        headers: header,
      }
    );
  }

  getShortsByFlowId(flowId: string): Observable<any> {
    const header = new HttpHeaders();
    header.append("Accept", "application/json");
    header.append("Content-Type", "application/json");
    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.getShortsByFlowIdUrl,
      flowId,
      {
        headers: header,
      }
    );
  }

  likeFlowShort(shortRowKey: string): Observable<any> {
    const header = new HttpHeaders();
    header.append("Accept", "application/json");
    header.append("Content-Type", "application/json");
    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.likeFlowShortUrl,
      { ShortRowKey: shortRowKey },
      {
        headers: header,
      }
    );
  }

  deleteFlowShort(rowKey: string): Observable<any> {
    const header = new HttpHeaders();
    header.append("Accept", "application/json");
    header.append("Content-Type", "application/json");
    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.deleteFlowShortUrl,
      rowKey,
      {
        headers: header,
      }
    );
  }

  getAllShorts(queryModel: FlowQueryModel): Observable<any> {
    const header = new HttpHeaders();
    header.append("Accept", "application/json");
    header.append("Content-Type", "application/json");
    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.getAllShortsUrl,
      queryModel,
      {
        headers: header,
      }
    );
  }

  createFlowReview(reviewModel: any): Observable<any> {
    const header = new HttpHeaders();
    header.append("Accept", "application/json");
    header.append("Content-Type", "application/json");
    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.createFlowReviewUrl,
      reviewModel,
      {
        headers: header,
      }
    );
  }

  getFlowReviews(flowModel: any): Observable<any> {
    const header = new HttpHeaders();
    header.append("Accept", "application/json");
    header.append("Content-Type", "application/json");
    console.log('getFlowReviews service called with:', flowModel);
    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.getFlowReviewsUrl,
     flowModel,
      {
        headers: header,
      }
    ).pipe(
      tap(response => console.log('getFlowReviews HTTP response:', response)),
      timeout(30000), // 30 second timeout to prevent hanging
      catchError(error => {
        console.error("Error in getFlowReviews:", error);
        // Only catch actual errors, not successful responses
        if (error.name === 'TimeoutError') {
          console.error("Request timed out after 30 seconds");
        }
        return of({ reviews: [] }); // Return empty reviews on error to prevent UI freeze
      }),
      finalize(() => console.log('getFlowReviews observable completed'))
    );
  }

  respondToReview(reviewRowKey: string, response: string): Observable<any> {
    const header = new HttpHeaders();
    header.append("Accept", "application/json");
    header.append("Content-Type", "application/json");
    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.respondToReviewUrl,
      { ReviewRowKey: reviewRowKey, Response: response },
      {
        headers: header,
      }
    );
  }

  respondToIssue(issueRowKey: string, response: string): Observable<any> {
    const header = new HttpHeaders();
    header.append("Accept", "application/json");
    header.append("Content-Type", "application/json");
    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.respondToIssueUrl,
      { IssueRowKey: issueRowKey, Response: response },
      {
        headers: header,
      }
    );
  }

  markReviewHelpful(reviewRowKey: string, markAsHelpful: boolean): Observable<any> {
    const header = new HttpHeaders();
    header.append("Accept", "application/json");
    header.append("Content-Type", "application/json");
    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.markReviewHelpfulUrl,
      { ReviewRowKey: reviewRowKey, MarkAsHelpful: markAsHelpful },
      {
        headers: header,
      }
    );
  }

  moderateReview(reviewRowKey: string, status: string): Observable<any> {
    const header = new HttpHeaders();
    header.append("Accept", "application/json");
    header.append("Content-Type", "application/json");
    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.moderateReviewUrl,
      { ReviewRowKey: reviewRowKey, Status: status },
      {
        headers: header,
      }
    );
  }

  moderateIssue(issueRowKey: string, status: string): Observable<any> {
    const header = new HttpHeaders();
    header.append("Accept", "application/json");
    header.append("Content-Type", "application/json");
    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.moderateIssueUrl,
      { IssueRowKey: issueRowKey, Status: status },
      {
        headers: header,
      }
    );
  }

  getCreatorReviews(flowRowKeys?: string[]): Observable<any> {
    const header = new HttpHeaders();
    header.append("Accept", "application/json");
    header.append("Content-Type", "application/json");
    // API will automatically get all flow row keys for logged-in user
    // No need to send body - API fetches flow row keys automatically
    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.getCreatorReviewsUrl,
      null, // Send null body - API doesn't need FlowRowKeys
      {
        headers: header,
      }
    );
  }

  getReviewsByUserEmail(userEmail: string, flowRowKeys: string[]): Observable<any> {
    const header = new HttpHeaders();
    header.append("Accept", "application/json");
    header.append("Content-Type", "application/json");
    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.getReviewsByUserEmailUrl,
      { UserEmail: userEmail, FlowRowKeys: flowRowKeys },
      {
        headers: header,
      }
    );
  }

  getFlowShorts(flowRowKey: string): Observable<any> {
    const header = new HttpHeaders();
    header.append("Accept", "application/json");
    header.append("Content-Type", "application/json");
    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.getFlowShortsUrl,
      { FlowRowKey: flowRowKey },
      {
        headers: header,
      }
    );
  }

  getUserShorts(userEmail: string, flowRowKeys: string[] = []): Observable<any> {
    const header = new HttpHeaders();
    header.append("Accept", "application/json");
    header.append("Content-Type", "application/json");
    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.getUserShortsUrl,
      { UserEmail: userEmail, FlowRowKeys: flowRowKeys },
      {
        headers: header,
      }
    );
  }

  updateReviewRating(reviewRowKey: string, rating: number): Observable<any> {
    const header = new HttpHeaders();
    header.append("Accept", "application/json");
    header.append("Content-Type", "application/json");
    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.updateReviewRatingUrl,
      { ReviewRowKey: reviewRowKey, Rating: rating },
      {
        headers: header,
      }
    );
  }

  // ─── Flow Collaboration ───────────────────────────────────────────────────

  sendCollabInvite(invite: {
    flowId: string;
    inviteeEmail: string;
    inviteMessage?: string;
    collaborationValue?: number;
  }): Observable<any> {
    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.sendCollabInviteUrl,
      invite
    );
  }

  acceptCollabInvite(flowId: string, responseMessage?: string): Observable<any> {
    let params = new HttpParams().set("flowId", flowId);
    if (responseMessage) params = params.set("responseMessage", responseMessage);
    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.acceptCollabInviteUrl,
      {},
      { params }
    );
  }

  rejectCollabInvite(flowId: string, responseMessage?: string): Observable<any> {
    let params = new HttpParams().set("flowId", flowId);
    if (responseMessage) params = params.set("responseMessage", responseMessage);
    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.rejectCollabInviteUrl,
      {},
      { params }
    );
  }

  cancelCollabInvite(flowId: string, inviteeEmail: string): Observable<any> {
    const params = new HttpParams()
      .set("flowId", flowId)
      .set("inviteeEmail", inviteeEmail);
    return this.http.delete<any>(
      environment.apiBaseUrl + Endpoint.cancelCollabInviteUrl,
      { params }
    );
  }

  getSentCollabInvites(): Observable<any> {
    return this.http.get<any>(
      environment.apiBaseUrl + Endpoint.sentCollabInvitesUrl
    );
  }

  getReceivedCollabInvites(): Observable<any> {
    return this.http.get<any>(
      environment.apiBaseUrl + Endpoint.receivedCollabInvitesUrl
    );
  }

  getCollabInvitesByFlow(flowId: string): Observable<any> {
    return this.http.get<any>(
      `${environment.apiBaseUrl}${Endpoint.invitesByFlowUrl}/${flowId}`
    );
  }
}
